/**
 * ThemeToggle — 모드(system/light/dark) + 그룹별 변형(variant) 분리 관리.
 *
 * 저장 구조 (localStorage):
 *   - admin-theme-mode      : 'system' | 'light' | 'dark'
 *   - admin-theme-light     : 라이트 그룹의 선택된 variant slug
 *   - admin-theme-dark      : 다크 그룹의 선택된 variant slug
 *
 * 적용 흐름:
 *   1) mode 결정 (저장값 또는 'system')
 *   2) 'system' 이면 OS prefers-color-scheme 으로 light/dark 그룹 결정
 *   3) 해당 그룹의 variant 를 <html data-theme="..."> 로 설정
 *      ※ 'light' 기본 variant 인 경우 data-theme 미설정 (:root 스타일 적용)
 *
 * 마크업 훅:
 *   - 헤더(시스템/Light/Dark)         : [data-theme-mode]
 *   - preferences 페이지(variant 카드)  : [data-theme-variant]   (group 은 자동 추론)
 *
 * 레거시 마이그레이션:
 *   - 'admin-theme' (단일 값) 에 저장돼 있던 옛 설정을 1회 변환해 옮긴 뒤 제거.
 */

type ThemeMode = "system" | "light" | "dark";
type LightVariant = "light" | "light-mongo" | "light-notion" | "light-figma" | "light-claude" | "light-apple" | "light-airtable" | "light-gumroad";
type DarkVariant  = "dark"  | "dark-mongo"  | "dark-notion"  | "dark-figma"  | "dark-claude"  | "dark-apple"  | "dark-airtable"  | "dark-gumroad";
type ThemeVariant = LightVariant | DarkVariant;

const LS_MODE  = "admin-theme-mode";
const LS_LIGHT = "admin-theme-light";
const LS_DARK  = "admin-theme-dark";
const LS_LEGACY = "admin-theme";

const LIGHT_VARIANTS: readonly LightVariant[] = ["light", "light-mongo", "light-notion", "light-figma", "light-claude", "light-apple", "light-airtable", "light-gumroad"];
const DARK_VARIANTS:  readonly DarkVariant[]  = ["dark",  "dark-mongo",  "dark-notion",  "dark-figma",  "dark-claude",  "dark-apple",  "dark-airtable",  "dark-gumroad"];

function isLightVariant(v: string): v is LightVariant { return (LIGHT_VARIANTS as readonly string[]).includes(v); }
function isDarkVariant(v: string): v is DarkVariant   { return (DARK_VARIANTS  as readonly string[]).includes(v); }
function variantGroup(v: ThemeVariant): "light" | "dark" { return isLightVariant(v) ? "light" : "dark"; }

/** 옛 단일 키(admin-theme)를 새 키 구조로 1회 마이그레이션. */
function migrateLegacy(): void {
  const legacy = localStorage.getItem(LS_LEGACY);
  if (!legacy) return;
  try {
    if (legacy === "system") {
      localStorage.setItem(LS_MODE, "system");
    } else if (isLightVariant(legacy)) {
      localStorage.setItem(LS_MODE, "light");
      localStorage.setItem(LS_LIGHT, legacy);
    } else if (isDarkVariant(legacy)) {
      localStorage.setItem(LS_MODE, "dark");
      localStorage.setItem(LS_DARK, legacy);
    }
  } finally {
    localStorage.removeItem(LS_LEGACY);
  }
}

function getMode(): ThemeMode {
  const v = localStorage.getItem(LS_MODE);
  return (v === "light" || v === "dark" || v === "system") ? v : "system";
}

function getLightVariant(): LightVariant {
  const v = localStorage.getItem(LS_LIGHT);
  return (v && isLightVariant(v)) ? v : "light";
}

function getDarkVariant(): DarkVariant {
  const v = localStorage.getItem(LS_DARK);
  return (v && isDarkVariant(v)) ? v : "dark";
}

/** 현재 설정으로부터 실제 적용될 variant 산출. */
function resolveActiveVariant(): ThemeVariant {
  const mode = getMode();
  if (mode === "light") return getLightVariant();
  if (mode === "dark")  return getDarkVariant();
  // system
  const prefersDark = window.matchMedia("(prefers-color-scheme: dark)").matches;
  return prefersDark ? getDarkVariant() : getLightVariant();
}

function applyVariant(v: ThemeVariant): void {
  const root = document.documentElement;
  if (v === "light") {
    // 기본 라이트는 :root 스타일 — data-theme 미설정
    root.removeAttribute("data-theme");
  } else {
    root.setAttribute("data-theme", v);
  }
  syncAriaState();
}

/** 헤더 모드 버튼과 preferences variant 카드의 aria-checked 상태 동기화. */
function syncAriaState(): void {
  const mode = getMode();
  const lightV = getLightVariant();
  const darkV  = getDarkVariant();

  document.querySelectorAll<HTMLElement>("[data-theme-mode]").forEach(el => {
    el.setAttribute("aria-checked", String(el.dataset.themeMode === mode));
  });

  document.querySelectorAll<HTMLElement>("[data-theme-variant]").forEach(el => {
    const v = el.dataset.themeVariant ?? "";
    const match = (isLightVariant(v) && v === lightV) || (isDarkVariant(v) && v === darkV);
    el.setAttribute("aria-checked", String(match));
  });
}

function setMode(mode: ThemeMode): void {
  localStorage.setItem(LS_MODE, mode);
  applyVariant(resolveActiveVariant());
}

function setVariant(v: ThemeVariant): void {
  if (isLightVariant(v)) {
    localStorage.setItem(LS_LIGHT, v);
  } else {
    localStorage.setItem(LS_DARK, v);
  }
  // variant 변경은 그 그룹이 활성일 때만 즉시 화면 갱신.
  // (다른 그룹의 variant 만 바꾼 경우 현재 화면엔 영향 없음 — aria 만 동기화)
  const mode = getMode();
  const activeGroup: "light" | "dark" =
    mode === "system"
      ? (window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light")
      : mode;
  if (variantGroup(v) === activeGroup) {
    applyVariant(v);
  } else {
    syncAriaState();
  }
}

export function initAdminThemeToggle(): void {
  migrateLegacy();

  // 페이지 로드 즉시 적용 (FOUC 방지는 layouts/main.php <head> inline script 가 1차 처리)
  applyVariant(resolveActiveVariant());

  // 헤더 모드 버튼
  document.querySelectorAll<HTMLElement>("[data-theme-mode]").forEach(el => {
    el.addEventListener("click", () => {
      const m = el.dataset.themeMode;
      if (m === "system" || m === "light" || m === "dark") {
        setMode(m);
        // details dropdown 닫기
        const menu = el.closest("details");
        if (menu) menu.removeAttribute("open");
      }
    });
  });

  // preferences variant 카드
  document.querySelectorAll<HTMLElement>("[data-theme-variant]").forEach(el => {
    el.addEventListener("click", () => {
      const v = el.dataset.themeVariant ?? "";
      if (isLightVariant(v) || isDarkVariant(v)) setVariant(v);
    });
  });

  // 시스템 다크모드 변경 감지 (mode === 'system' 일 때만)
  window.matchMedia("(prefers-color-scheme: dark)").addEventListener("change", () => {
    if (getMode() === "system") applyVariant(resolveActiveVariant());
  });
}
